import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, effect, inject, input, signal, ViewEncapsulation } from '@angular/core';
import { HxButton } from '../button/button';
import { HxCheckbox } from '../checkbox/checkbox';
import { HxDialogRef } from '../dialog/dialog-ref';
import { HxDialogComponent } from '../dialog/dialog.component';
import { DIALOG_REF } from '../dialog/internal/dialog-injectors';
import type { ColumnDefinition } from './types';

/** Result data returned when the column customization dialog closes */
export type ColumnCustomizationResult = {
  /** Whether the user confirmed the changes (Done) or cancelled */
  confirmed: boolean;
  /** Selected column fields (only populated when confirmed is true) */
  selections: Set<string>;
};

/**
 * Column Customization Dialog Component
 *
 * Provides a full-height dialog interface for customizing visible columns in managed-table. Displays a list of
 * checkboxes for each column with Select All functionality.
 *
 * @example
 *   ```typescript
 *   const dialogRef = this.dialogService.open(HxColumnCustomizationDialogComponent, {
 *     inputs: {
 *       columns: tableConfig.mainSchema,
 *       selectedFields: visibleColumnFields
 *     },
 *     position: 'right',
 *     width: '300px',
 *     height: '100vh'
 *   });
 *
 *   dialogRef.afterClosed().subscribe(result => {
 *     if (result?.confirmed) {
 *       this.visibleColumnFields.set(result.selections);
 *     }
 *   });
 *   ```;
 */
@Component({
  selector: 'hx-column-customization-dialog',
  standalone: true,
  imports: [CommonModule, HxButton, HxCheckbox, HxDialogComponent],
  templateUrl: './column-customization-dialog.html',
  styleUrls: ['./column-customization-dialog.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class HxColumnCustomizationDialogComponent {
  /** Column schema from managed table configuration */
  readonly columns = input<ColumnDefinition[]>([]);

  /** Currently selected/visible column fields */
  readonly selectedFields = input<Set<string>>(new Set());

  /** Reference to the dialog instance - injected via DIALOG_REF token */
  private readonly dialogRef =
    inject<HxDialogRef<HxColumnCustomizationDialogComponent, ColumnCustomizationResult>>(DIALOG_REF);

  /** Temporary selections while dialog is open (local state) */
  readonly tempSelections = signal<Set<string>>(new Set());

  /** Track if we've initialized (to do it only once) */
  private initialized = false;

  constructor() {
    // Initialize temp selections with current selected fields when inputs are available
    effect(() => {
      const initial = this.selectedFields();
      const cols = this.columns();

      // Only initialize once when we have column schema
      // (initial can be empty if no columns are selected, which is valid)
      if (!this.initialized && cols.length > 0) {
        this.tempSelections.set(new Set(initial));
        this.initialized = true;
      }
    });
  }

  /** Check if Select All should be checked */
  isSelectAllChecked(): boolean {
    const schema = this.columns();
    const temp = this.tempSelections();
    return schema.length > 0 && schema.every((col) => temp.has(col.field));
  }

  /** Check if a specific column is currently selected */
  isColumnSelected(field: string): boolean {
    return this.tempSelections().has(field);
  }

  /** Handle individual column checkbox change */
  handleColumnCheckboxChange(field: string, selectedValues: (string | number)[]): void {
    const isChecked = selectedValues.includes(field);

    const temp = new Set(this.tempSelections());
    if (isChecked) {
      temp.add(field);
    } else {
      temp.delete(field);
    }
    this.tempSelections.set(temp);
  }

  /** Handle Select All checkbox change */
  handleSelectAllChange(selectedValues: (string | number)[]): void {
    const isChecked = selectedValues.includes('select-all');

    if (isChecked) {
      // Select all columns
      const schema = this.columns();
      this.tempSelections.set(new Set(schema.map((col) => col.field)));
    } else {
      // Deselect all columns
      this.tempSelections.set(new Set());
    }
  }

  /** Reset all columns to visible */
  resetColumns(): void {
    const schema = this.columns();
    this.tempSelections.set(new Set(schema.map((col) => col.field)));
  }

  /** Apply changes and close dialog */
  applyChanges(): void {
    this.dialogRef.close({
      confirmed: true,
      selections: new Set(this.tempSelections()),
    });
  }

  /** Cancel changes and close dialog */
  cancel(): void {
    this.dialogRef.close({
      confirmed: false,
      selections: new Set(),
    });
  }
}
